import { useState, useEffect, useRef } from "react";
import { site } from "../../data/site";

type FeaturedImageProps = {
  src: string;
  alt?: string;
  className?: string;
  loading?: "eager" | "lazy";
  timeoutMs?: number;
};

export function FeaturedImage({
  src,
  alt = "",
  className = "",
  loading = "lazy",
  timeoutMs = 3000 // Fallback if image load takes > 3 seconds
}: FeaturedImageProps) {
  const [currentSrc, setCurrentSrc] = useState(src);
  const [hasFailedOrTimedOut, setHasFailedOrTimedOut] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Reset state when the src prop changes
  useEffect(() => {
    setCurrentSrc(src);
    setHasFailedOrTimedOut(false);
  }, [src]);

  useEffect(() => {
    if (hasFailedOrTimedOut) {
      setCurrentSrc(site.defaultImage);
      return;
    }

    // Set a load timeout timer
    timerRef.current = setTimeout(() => {
      console.warn(`Image load timed out after ${timeoutMs}ms: ${src}`);
      setHasFailedOrTimedOut(true);
    }, timeoutMs);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [src, timeoutMs, hasFailedOrTimedOut]);

  const handleLoad = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
  };

  const handleError = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    console.error(`Image load failed: ${src}`);
    setHasFailedOrTimedOut(true);
  };

  return (
    <img
      src={currentSrc}
      alt={alt}
      className={className}
      loading={loading}
      onLoad={handleLoad}
      onError={handleError}
    />
  );
}
